/**
 * WP-04B-R — Client lifecycle hook that reconciles the authenticated account
 * and reports a REQUIRED gate phase for the `/app` subtree.
 *
 * Runs exactly once per authenticated identity per browser session. It sends no
 * subject: the server derives it from the verified bearer token. A retryable
 * outcome is attempted exactly once more (one bounded retry); terminal outcomes
 * are never retried. The hook never surfaces SQL, provider messages, tokens,
 * service-role details, or raw exceptions — only the stable phase vocabulary.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { bootstrapAccount } from "./account.functions";
import type { AccountBootstrapOutcome } from "./bootstrapCommand";
import { phaseForOutcome, shouldRetryOutcome, type AccountGatePhase } from "./gate";

export interface AccountBootstrapLifecycleOptions {
  /** The verified identity, or null while unknown / signed out. */
  readonly userId: string | null;
  /** Optional creation-time time zone; ignored on a retry by the database. */
  readonly timeZone?: string | null;
  readonly displayName?: string | null;
}

export interface AccountBootstrapGate {
  readonly phase: AccountGatePhase;
  /** Re-arms the gate from a recoverable `unavailable` state. */
  readonly retry: () => void;
}

export function useAccountBootstrap(
  options: AccountBootstrapLifecycleOptions,
): AccountBootstrapGate {
  const { userId, timeZone, displayName } = options;
  const run = useServerFn(bootstrapAccount);
  const attempted = useRef<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<AccountBootstrapOutcome | null>(null);

  useEffect(() => {
    if (!userId) {
      setOutcome(null);
      return;
    }
    const key = `${userId}:${attempt}`;
    if (attempted.current === key) return;
    attempted.current = key;

    let active = true;
    setOutcome(null);

    const call = async (): Promise<AccountBootstrapOutcome> =>
      run({
        data: {
          ...(displayName === undefined ? {} : { displayName }),
          ...(timeZone === undefined ? {} : { timeZone }),
        },
      });

    void (async () => {
      try {
        let settled = await call();
        // Exactly one bounded retry, and only for recoverable outcomes.
        if (shouldRetryOutcome(settled)) settled = await call();
        if (active) setOutcome(settled);
      } catch {
        // Deliberately opaque: never surface transport or provider detail.
        if (active) setOutcome({ ok: false, reason: "unavailable", retryable: true });
      }
    })();

    return () => {
      active = false;
    };
  }, [userId, attempt, timeZone, displayName, run]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { phase: phaseForOutcome(outcome), retry };
}
